import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  List,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Button,
} from "@material-ui/core";
import { ItemType, HeadingType, ItemIdType } from "../types";
import { ExpandMore } from "@material-ui/icons";
import {
  handleChangeMultiple,
  isInactiveValueSelected,
  toSafeKey,
} from "./helpers";

type SelectMultipleCheckboxDialogProps = {
  dialogOpen: boolean;
  children: (ItemType | HeadingType)[];
  selectedItems: ItemIdType[];
  onClose: () => void;
  dialogTitle?: string;
  datatest?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  label?: string;
};

export const SelectMultipleCheckboxDialog: React.FC<SelectMultipleCheckboxDialogProps> = ({
  dialogOpen,
  children,
  dialogTitle,
  datatest,
  onClose,
  onChange,
  selectedItems,
  name,
}) => {
  const scrollableContentRef = useRef<HTMLElement>();

  const checkboxList = buildCheckboxList(children, selectedItems);

  return (
    <Dialog
      data-test={`dialog-${datatest}`}
      open={dialogOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        style: {
          minHeight: "80vh", // stop the dialog resizing as list grows/shrinks
          maxHeight: "80vh",
        },
      }}
    >
      {dialogTitle ? <DialogTitle>{dialogTitle}</DialogTitle> : null}

      <DialogContent ref={scrollableContentRef}>
        <Grid container spacing={3}>
          <Grid item sm={12}></Grid>
          <Grid item sm={12}>
            <div style={{ minHeight: "100vh" }}>
              {!children.length ? (
                <Typography>Please wait...</Typography>
              ) : null}

              {checkboxList.isAccordionMode ? (
                <div>
                  {checkboxList.accordionizedItems.map((accordionGroup) => (
                    <Accordion key={accordionGroup.heading}>
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                        data-test={`${datatest}-category`}
                      >
                        <Typography>
                          {accordionGroup.heading}{" "}
                          {accordionGroup.items.filter((item) =>
                            selectedItems.includes(item.id)
                          ).length > 0 &&
                            `(${
                              accordionGroup.items.filter((item) =>
                                selectedItems.includes(item.id)
                              ).length
                            })`}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails
                        style={{ display: "inherit" }}
                        data-test={`${datatest}-checkboxes`}
                      >
                        {accordionGroup.items.map((item) => (
                          <div key={toSafeKey(item.id)}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedItems.includes(item.id)}
                                  onChange={(_, checked) => {
                                    handleChangeMultiple(
                                      name,
                                      item,
                                      selectedItems,
                                      !checked,
                                      onChange
                                    );
                                  }}
                                  disabled={item.disabled}
                                />
                              }
                              label={item.label}
                            />
                          </div>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </div>
              ) : null}

              {!checkboxList.isAccordionMode ? (
                <div>
                  {checkboxList.items.map((item) => (
                    <div key={toSafeKey(item.id)}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedItems.includes(item.id)}
                            onChange={(_, checked) => {
                              handleChangeMultiple(
                                name,
                                item,
                                selectedItems,
                                !checked,
                                onChange
                              );
                            }}
                            disabled={item.disabled}
                          />
                        }
                        label={item.label}
                      />
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button
          size="small"
          onClick={onClose}
          color="primary"
          data-test={`${datatest}-close-button`}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type CheckboxListType = {
  isAccordionMode: boolean;
  accordionizedItems: { heading: string; items: ItemType[] }[];
  items: ItemType[];
};

const buildCheckboxList = (
  children: (ItemType | HeadingType)[],
  selectedItems: ItemIdType[]
): CheckboxListType => {
  const isAccordionMode =
    children && children.length && (children[0] as HeadingType).isHeading;

  if (!isAccordionMode) {
    return {
      isAccordionMode: false,
      accordionizedItems: [],
      items: children.map((item) => item as ItemType),
    };
  } else {
    const accordionizedItems: { heading: string; items: ItemType[] }[] = [];

    for (const item of children) {
      const itemAsHeading = item as HeadingType;
      const itemAsItem = item as ItemType;
      const itemIsHeading = itemAsHeading.isHeading;

      if (itemIsHeading) {
        accordionizedItems.push({ heading: itemAsHeading.label, items: [] });
      }
      // edge case - if there are checkboxes before the first heading
      if (!accordionizedItems.length) {
        accordionizedItems.push({ heading: "Unknown", items: [] });
      }
      if (!itemIsHeading) {
        const isInactiveValue = isInactiveValueSelected(
          itemAsItem.id,
          selectedItems
        );
        const thisGroup = accordionizedItems[accordionizedItems.length - 1];
        if (itemAsItem.active !== false || isInactiveValue)
          thisGroup.items.push(itemAsItem);
      }
    }

    return {
      isAccordionMode: true,
      accordionizedItems,
      items: [],
    };
  }
};
